import { DialogTitle } from "@headlessui/react";
import { Tag } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useFetcher, useSearchParams } from "react-router";

import type { EventFragment } from "~/__generated__/graphql/graphql";

import { useOptionalParticipationFlow } from "~/components/participation/ParticipationFlow";
import { PrimaryButton } from "~/components/Button";
import { StepIndicator } from "~/components/plans/components/StepIndicator";
import { Text } from "~/components/Text";
import { useRootLocale } from "~/root";
import type { TicketQuoteResult } from "~/routes/api.events.$eventId.ticket-quote";
import { centsToMoney } from "~/utils/money";
import { CheckoutFooter } from "./CheckoutFooter";

import { useCheckoutSession } from "./CheckoutSession";
import { availableCheckoutDraft, restoreCheckoutURL, type CheckoutStage } from "./checkout-state";
import { DiscountCodeInput } from "./DiscountCodeInput";

import { GroupTicketSelection } from "./GroupTicketSelection";
import { groupSelectionAvailable, groupQuoteSelection, groupTicketVariations } from "./group-checkout";
import { TicketVariationSelector } from "./TicketVariationSelector";
import {
  getSelectedTicketVariation,
  TICKET_VARIATION_PARAM,
} from "./ticketVariations";

type Props = {
  plan: EventFragment;
  ticketVariationsEnabled: boolean;
  nextStep?: CheckoutStage;
};

export function CheckoutTicketsStep({
  plan,
  ticketVariationsEnabled,
  nextStep = "details",
}: Props) {
  const locale = useRootLocale();
  const observe = useOptionalParticipationFlow()?.observe;
  const observation = useRef<ReturnType<NonNullable<typeof observe>>>(undefined);
  const checkout = useCheckoutSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const groupEnabled = ticketVariationsEnabled && plan.allowGroupRSVPs && plan.maxTicketsPerPurchase > 1 && plan.myRequest?.state !== "IN_ACCEPTED_WAITLIST";
  const selectionPlan = groupEnabled
    ? { ...plan, ticketVariations: groupTicketVariations(plan.ticketVariations, checkout.draft.groupSlots) }
    : plan;
  const initialVariation = ticketVariationsEnabled
    ? getSelectedTicketVariation(selectionPlan, searchParams.get(TICKET_VARIATION_PARAM))
    : undefined;
  const [selectedVariationID, setSelectedVariationID] = useState<string | null>(
    initialVariation?.id ?? null,
  );
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);

  const checkedSavedSelection = useRef(false);
  const restoredDraft = checkedSavedSelection.current
    ? checkout.draft
    : availableCheckoutDraft(checkout.draft, plan);
  const restarting = restoredDraft !== checkout.draft;
  useEffect(() => {
    if (!checkout.ready || checkedSavedSelection.current) return;
    checkedSavedSelection.current = true;
    if (!restarting) return;
    checkout.update(restoredDraft);
    setSelectedVariationID(getSelectedTicketVariation(plan, null)?.id ?? null);
    setSearchParams(previous => restoreCheckoutURL(previous.toString(), restoredDraft), { replace: true, preventScrollReset: true });
  }, [checkout, restoredDraft, restarting, plan, setSearchParams]);

  const selection = groupQuoteSelection(checkout.draft.groupSlots, checkout.draft.purchaserSlotID);
  const groupSelection = checkout.draft.groupSlots.length > 1;
  const additionalTicketsKey = groupSelection && selection ? JSON.stringify(selection.additionalTickets) : "";
  const selectionValid = !groupSelection || groupEnabled && !!selection && groupSelectionAvailable(checkout.draft.groupSlots, plan);
  const discountCode = searchParams.get("discountCode")?.trim() ?? "";
  const selectedVariation = ticketVariationsEnabled
    ? getSelectedTicketVariation(selectionPlan, selectedVariationID)
    : undefined;
  const ticketVariationID = selectedVariation?.id;
  const hasTicket = !ticketVariationsEnabled || !!selectedVariation;

  const quoteFetcher = useFetcher<TicketQuoteResult>({
    key: `ticket-quote:${plan.id}`,
  });

  const quoteParams = new URLSearchParams();
  if (ticketVariationID) {
    quoteParams.set(TICKET_VARIATION_PARAM, ticketVariationID);
  }
  if (discountCode) quoteParams.set("discountCode", discountCode);
  const quoteURL = `/api/events/${plan.id}/ticket-quote?${quoteParams}`;
  const loadQuote = quoteFetcher.load;
  const submitQuote = quoteFetcher.submit;

  const requestQuote = useCallback(() => {
    if (restarting || !hasTicket || !selectionValid) return;
    observation.current?.finish("unknown");
    observation.current = observe?.("quote");
    if (additionalTicketsKey) {
      submitQuote({ ticketVariationID: ticketVariationID!, additionalTickets: additionalTicketsKey, discountCode }, { method: "post", action: `/api/events/${plan.id}/ticket-quote` });
    } else loadQuote(quoteURL);
  }, [restarting, hasTicket, selectionValid, additionalTicketsKey, ticketVariationID, discountCode, plan.id, loadQuote, submitQuote, quoteURL, observe]);
  useEffect(() => { requestQuote(); }, [requestQuote]);

  const quoteData = quoteFetcher.data;
  const quoteMatchesSelection =
    quoteData !== undefined &&
    quoteData.ticketVariationID === (ticketVariationID ?? null) &&
    quoteData.discountCode === discountCode &&
    (quoteData.additionalTicketsKey ?? "") === additionalTicketsKey;
  const quote = quoteMatchesSelection && quoteData.ok ? quoteData.ticketCost : null;
  const quoteError =
    quoteFetcher.state === "idle" && quoteMatchesSelection
      ? quoteData.error
      : null;
  const quoteIsLoading =
    hasTicket &&
    selectionValid &&
    (quoteFetcher.state !== "idle" ||
      !quoteMatchesSelection ||
      (!quote && !quoteError));
  const displayedQuote = quote ?? (quoteIsLoading && quoteData?.ok ? quoteData.ticketCost : null);

  useEffect(() => {
    if (quoteIsLoading || !quoteMatchesSelection) return;
    observation.current?.finish(quote ? "success" : "failure", quote ? undefined : "unavailable");
    observation.current = undefined;
  }, [quoteIsLoading, quoteMatchesSelection, quote]);

  const handleContinue = () => {
    if (restarting || !selectionValid || !quote?.available || quoteIsLoading || isApplyingDiscount) return;
    checkout.update({ ticketVariationID: ticketVariationID ?? "" });
    checkout.setStep(nextStep);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col sm:block">
      <div className="shrink-0">
        <StepIndicator currentStep={1} showTicketStep />
      </div>

      <div className="-mx-6 mt-[10px] min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pt-[30px] pb-2 sm:mx-0 sm:overflow-visible sm:px-0 sm:pb-6">
        <Text as={DialogTitle} variant="primary">
          Choose your ticket
        </Text>
        <div className="mt-2.5">
          {groupEnabled ? <GroupTicketSelection plan={plan} selectedVariationID={selectedVariationID} onSelect={id => {
            setSelectedVariationID(id);
            checkout.update({ ticketVariationID: id });
            setSearchParams(previous => { const next = new URLSearchParams(previous); next.set(TICKET_VARIATION_PARAM, id); return next; }, { replace: true, preventScrollReset: true });
          }} /> : ticketVariationsEnabled ? (
            <TicketVariationSelector
              plan={plan}
              enabled
              selectedVariationID={selectedVariationID}
              onSelect={(id) => {
                setSelectedVariationID(id);
                checkout.update({ ticketVariationID: id, groupSlots: [], purchaserSlotID: "", paymentPhase: "overview" });
                setSearchParams(previous => { const next = new URLSearchParams(previous); next.set(TICKET_VARIATION_PARAM, id); return next; }, { replace: true, preventScrollReset: true });
              }}
            />
          ) : (
            <section aria-label="Your ticket">
              <div className="flex w-full items-center gap-3 rounded-xl bg-white p-[0.6rem] text-left text-butter-green sm:p-[0.8rem]">
                <span
                  aria-hidden
                  className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-butter-cream text-xl"
                >
                  🎟️
                </span>
                <span className="min-w-0 flex-1 font-sans text-base font-bold">
                  Full access
                </span>
                {typeof plan.regularTicketCost?.totalCostCents === "number" && (
                  <span className="shrink-0 font-serif text-base leading-tight font-bold">
                    {centsToMoney(
                      plan.regularTicketCost.totalCostCents,
                      "$",
                      locale,
                    )}
                  </span>
                )}
              </div>
            </section>
          )}
        </div>

        {groupSelection && !groupEnabled && <Text role="alert" size="sm" className="mt-3">Group bookings are no longer available for this selection. Choose a single ticket to review a new checkout.</Text>}
        {groupSelection && groupEnabled && !selectionValid && <Text role="alert" size="sm" className="mt-3">Some tickets are no longer available. Update your selection to continue.</Text>}
        {!hasTicket && selectionValid && (
          <Text as="p" variant="secondary" size="sm" className="mt-3">
            Choose an available ticket to continue.
          </Text>
        )}


        {hasTicket && (
          <section className="mt-5" aria-labelledby="your-bag-heading">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <Text
                id="your-bag-heading"
                as="h2"
                variant="secondary"
                size="md"
                weight="bold"
              >
                Your bag
              </Text>
              <div aria-live="polite" aria-busy={quoteIsLoading}>
                {displayedQuote ? (
                  <Text as="span" variant="secondary" size="md" weight="bold">
                    {centsToMoney(displayedQuote.totalCostCents, "$", locale)}{" "}
                    <span className="font-sans text-sm font-normal">incl. fees</span>
                  </Text>
                ) : null}
              </div>
            </div>

            {(!!discountCode || !groupSelection || plan.allowGroupDiscountCodes && selectionValid) && (
              <DiscountCodeInput
                groupQuote={groupSelection && selection ? { ticketVariationID: ticketVariationID!, additionalTickets: selection.additionalTickets } : undefined}
                eventId={plan.id}
                eventTitle={plan.title}
                appliedCode={discountCode}
                onApplyingChange={setIsApplyingDiscount}
              />
            )}

            {displayedQuote && displayedQuote.totalDiscountCents > 0 && (
              <div className="mt-4 flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-4 font-sans text-base text-butter-green">
                  <span>Sub-total</span>
                  <span>
                    {centsToMoney(
                      displayedQuote.originalTotalCents ??
                        displayedQuote.totalCostCents + displayedQuote.totalDiscountCents,
                      "$",
                      locale,
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4 font-sans text-base text-butter-green">
                  <span className="inline-flex items-center gap-1.5">
                    Discount
                    <Tag className="size-3.5" aria-hidden />
                  </span>
                  <span>{centsToMoney(displayedQuote.totalDiscountCents, "$", locale)}</span>
                </div>
                <div className="flex items-center justify-between gap-4 font-sans text-base font-bold text-butter-green">
                  <span>Total</span>
                  <span className="inline-flex items-center gap-2">
                    {displayedQuote.originalTotalCents !== null && (
                      <span className="font-normal line-through opacity-60">
                        {centsToMoney(displayedQuote.originalTotalCents, "$", locale)}
                      </span>
                    )}
                    <span>{centsToMoney(displayedQuote.totalCostCents, "$", locale)}</span>
                  </span>
                </div>
                <p className="text-right font-sans text-sm font-normal text-butter-lime">
                  You&apos;re saving{" "}
                  {centsToMoney(displayedQuote.totalDiscountCents, "$", locale)}
                </p>
              </div>
            )}

            {quoteError && (
              <div className="mt-4 font-sans text-sm text-butter-pink">
                <p role="alert">{quoteError}</p>
                <button
                  type="button"
                  className="mt-1 font-bold text-butter-green underline"
                  onClick={requestQuote}
                >
                  Try again
                </button>

              </div>
            )}
          </section>
        )}
      </div>

      <CheckoutFooter showTerms={false}>
        <PrimaryButton
          type="button"
          className="text-base!"
          loading={(selectionValid && quoteIsLoading) || isApplyingDiscount}
          disabled={
            restarting || !hasTicket || !selectionValid ||
            !quote?.available ||
            quoteIsLoading ||
            isApplyingDiscount
          }
          onClick={handleContinue}
        >
          Next
        </PrimaryButton>


      </CheckoutFooter>
    </div>
  );
}
